import { NgTemplateOutlet, SlicePipe } from '@angular/common';
import { Component, input, model, TemplateRef, viewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DropdownPosition, NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { EnhancedOption } from '@shared/types/components.type';

@Component({
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgSelectModule,
    FormsModule,
    CheckboxComponent,
    SlicePipe,
    NgTemplateOutlet,
    TooltipDirective,
  ],
  selector: 'aux-multi-checkbox-select',
  styles: [
    `
      .ng-select-multiple-checkbox {
        .ng-optgroup,
        .ng-option.ng-option-child,
        .ng-option:not(.ng-option-disabled),
        .ng-dropdown-footer {
          padding: 0 !important;
          color: var(--aux-black);
          line-height: 16px;
        }

        .ng-optgroup {
          border-bottom: 1px solid var(--aux-gray-dark);
        }

        .ng-dropdown-footer {
          border-top: 1px solid var(--aux-gray-dark);
        }

        & .ng-dropdown-panel .ng-dropdown-panel-items .ng-option:last-child {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        .ng-option,
        .ng-optgroup {
          &::after {
            content: none;
          }
          &.ng-option-marked {
            background-color: var(--aux-hover) !important;
          }
          &.ng-option-selected:not(.ng-option-marked) {
            background-color: #fff;
          }
        }
      }
    `,
  ],
  template: `
    <ng-select
      #select
      (add)="addItem($event)"
      [multiple]="true"
      [clearable]="clearable()"
      [searchable]="true"
      [loading]="loading()"
      [placeholder]="placeholder()"
      [(ngModel)]="selectedItems"
      [selectableGroup]="true"
      [selectableGroupAsModel]="false"
      [closeOnSelect]="false"
      bindValue="value"
      groupBy="groupField"
      class="ng-select-multiple-checkbox"
      [dropdownPosition]="dropdownPosition()"
    >
      <ng-template ng-optgroup-tmp let-item="item" let-item$="item$" let-index="index">
        <div class="mx-[16px] py-[16px] font-normal">
          <aux-checkbox
            class=""
            [checked]="item$.selected"
            [disabled]="!!item.disabled"
            [auxTooltip]="item.tooltip || ''"
            [indeterminate]="
              selectedItems().length > 0 && selectedItems().length < options().length
            "
          >
            <span class="ml-[3px]">Select All</span>
          </aux-checkbox>
        </div>
      </ng-template>
      <ng-template ng-option-tmp let-item="item" let-item$="item$" let-index="index">
        <aux-checkbox
          class="p-[16px]"
          [checked]="item$.selected"
          [disabled]="!!item.disabled"
          [auxTooltip]="item.tooltip || ''"
          [class.cursor-not-allowed]="!!item.disabled"
        >
          <p
            class="ml-[3px] truncate"
            [class.leading-5]="!!item.subLabel"
            [class.font-bold]="!!item.subLabel"
            #optionLabel
            [auxTooltip]="
              optionLabel.offsetWidth < optionLabel.scrollWidth && !item.tooltip ? item.label : ''
            "
          >
            {{ item.label }}
          </p>
          @if (item.subLabel) {
            <p class="truncate italic leading-5">{{ item.subLabel }}</p>
          }
        </aux-checkbox>
      </ng-template>

      @if (footerRef()) {
        <ng-template ng-footer-tmp>
          <ng-container [ngTemplateOutlet]="footerRef()!"></ng-container>
        </ng-template>
      }

      <ng-template ng-multi-label-tmp let-items="items" let-clear="clear">
        @for (item of items | slice: 0 : itemsToDisplay(); track $any(item).value) {
          <div class="ng-value flex">
            <span
              class="ng-value-label inline-block max-w-[100px] truncate"
              #selectedItemLabel
              [auxTooltip]="
                selectedItemLabel.offsetWidth < selectedItemLabel.scrollWidth
                  ? $any(item).label
                  : ''
              "
            >
              {{ $any(item).label }}
            </span>
            @if (labelClearable()) {
              <span class="ng-value-icon right" (click)="clear(item)" aria-hidden="true">×</span>
            }
          </div>
        }
        @if (items.length > itemsToDisplay()) {
          <div class="ng-value">
            <span class="ng-value-label">{{ items.length - itemsToDisplay() }} more...</span>
          </div>
        }
      </ng-template>
      @for (option of options(); track option.value) {
        <ng-option [value]="option">{{ option.label }}</ng-option>
      }
    </ng-select>
  `,
})
export class MultiCheckboxSelectComponent {
  selectedItems = model<string[]>([]);

  clearable = input<boolean>(false);
  dropdownPosition = input<DropdownPosition>('auto');
  footerRef = input<null | TemplateRef<unknown>>(null);
  itemsToDisplay = input<number>(2);
  labelClearable = input<boolean>(true);
  loading = input<boolean>(false);
  options = input.required({
    transform: this.transformOptions,
  });
  placeholder = input<string>('');

  select = viewChild<NgSelectComponent>('select');

  // ng-select selects items even if controller is disabled just by clicking on the element
  addItem(item: EnhancedOption) {
    if (item.disabled) {
      this.selectedItems.set(this.selectedItems().filter((i) => i !== item.value));
    }
  }

  selectAll() {
    this.selectedItems.set(
      this.options()
        .filter((item) => !item.disabled)
        .map((option) => option.value)
    );
  }

  transformOptions(options: EnhancedOption[]): (EnhancedOption & { groupField: null | string })[] {
    // this is a little hack to make ng-select work with groups
    // we need to add a groupField to each option
    // then add groupBy="groupField" in the ng-select template
    // this way we can have the "select all" option
    return options.map((option) => ({ ...option, groupField: option.disabled ? null : 'all' }));
  }
}
